<script setup lang="ts">
import { useToast } from '@/components/ui/toast/use-toast'

const props = defineProps<{
	members: App.Data.Team.TeamMemberData[]
	invitations: App.Data.Team.TeamInvitationData[]
	canAddTeamMembers: boolean
	canRemoveTeamMembers: boolean
}>()

const { user } = useUser()
const { toast } = useToast()

const inviteForm = useForm({
	method: 'POST',
	url: route('team-members.store'),
	fields: {
		email: '',
	},
	errorBag: 'addTeamMember',
})

const cancelInvitation = ref<App.Data.Team.TeamInvitationData | null>(null)
const showCancelInvitationDialog = computed<boolean>(() => !!cancelInvitation.value)
const confirmCancelInvitation = function () {
	if (!cancelInvitation.value) {
		return
	}

	useForm({
		method: 'DELETE',
		url: route('team-invitations.destroy', { invitation: cancelInvitation.value.id }),
		fields: {},
		hooks: {
			success() {
				cancelInvitation.value = null

				toast({
					title: t('app.invitation_cancelled'),
					variant: 'successful',
				})
			},
		},
	}).submit()
}

const removeTeamMember = ref<App.Data.Team.TeamMemberData | null>(null)
const showRemoveTeamMemberDialog = computed<boolean>(() => !!removeTeamMember.value)
const confirmRemoveTeamMember = function () {
	if (!removeTeamMember.value) {
		return
	}

	useForm({
		method: 'DELETE',
		url: route('team-members.destroy', { userUuid: removeTeamMember.value.uuid }),
		fields: {},
		errorBag: 'removeTeamMember',
		hooks: {
			success() {
				removeTeamMember.value = null
			},
		},
	}).submit()
}
</script>


					<script lang="ts">
					import __hybridly_layout_0 from '~/resources/layouts/default.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
	<content-wrapper>
		<team-submenu />

		<div class="mb-4">
			<h2 class="text-primary mb-4 rounded border-b py-2 text-2xl font-bold">
				{{ t('app.members') }}
			</h2>

			<div class="flex max-w-xl flex-col gap-3">
				<div v-for="member in props.members" :key="member.uuid" class="bg-sidebar flex items-center gap-3 rounded p-2">
					<ui-avatar-initials :name="member.name" class="size-8 shrink-0 rounded-sm" />

					<div class="flex grow flex-wrap items-center justify-between gap-3 text-sm">
						<span>{{ member.name }}</span>
						<ui-button
							v-if="props.canRemoveTeamMembers && member.uuid !== user.uuid"
							variant="link"
							class="text-destructive"
							@click="removeTeamMember = member"
						>
							{{ t('app.buttons.remove') }}
						</ui-button>
					</div>
				</div>
			</div>
		</div>

		<div v-if="canAddTeamMembers">
			<h2 class="text-primary mb-4 rounded border-b py-2 text-2xl font-bold">
				{{ t('app.invitations') }}
			</h2>

			<div class="max-w-xl">
				<form class="flex gap-3" @submit.prevent="inviteForm.submit">
					<div class="w-full">
						<ui-input
							v-model="inviteForm.fields.email"
							:placeholder="t('validation.attributes.email')"
							required
							type="email"
							class="grow"
						/>
						<ui-form-error :message="inviteForm.errors.email" />
					</div>
					<ui-button type="submit" :loading="inviteForm.processing">
						{{ t('app.buttons.invite') }}
						<template #icon>
							<i-ph-arrow-right />
						</template>
					</ui-button>
				</form>

				<div class="mt-3 flex flex-col gap-3">
					<div v-for="invitation in props.invitations" :key="invitation.id" class="bg-sidebar flex flex-wrap items-center justify-between gap-3 rounded p-2 text-sm">
						<span>{{ invitation.email }}</span>
						<ui-button variant="link" class="text-destructive" @click="cancelInvitation = invitation">
							{{ t('app.buttons.cancel') }}
						</ui-button>
					</div>
				</div>
			</div>
		</div>

		<ui-dialog v-model:open="showRemoveTeamMemberDialog">
			<ui-dialog-content @interact-outside="removeTeamMember = null" @escape-key-down="removeTeamMember = null">
				<ui-dialog-title>
					{{ t('app.remove_team_member_dialog_title') }}
				</ui-dialog-title>

				<ui-dialog-description>
					{{ t('app.remove_team_member_dialog_text') }}

					<div class="mt-3 flex flex-wrap gap-3">
						<ui-button variant="destructive" @click="confirmRemoveTeamMember">
							{{ t('app.buttons.confirm') }}
						</ui-button>
					</div>
				</ui-dialog-description>

				<ui-dialog-close
					class="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none"
					@click="removeTeamMember = null"
				>
					<i-ph-x-bold class="size-4" />
				</ui-dialog-close>
			</ui-dialog-content>
		</ui-dialog>

		<ui-dialog v-model:open="showCancelInvitationDialog">
			<ui-dialog-content @interact-outside="cancelInvitation = null" @escape-key-down="cancelInvitation = null">
				<ui-dialog-title>
					{{ t('app.invitation_cancel_dialog_title') }}
				</ui-dialog-title>

				<ui-dialog-description>
					{{ t('app.invitation_cancel_dialog_text') }}

					<div class="mt-3 flex flex-wrap gap-3">
						<ui-button variant="destructive" @click="confirmCancelInvitation">
							{{ t('app.buttons.confirm') }}
						</ui-button>
					</div>
				</ui-dialog-description>

				<ui-dialog-close
					class="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none"
					@click="cancelInvitation = null"
				>
					<i-ph-x-bold class="size-4" />
				</ui-dialog-close>
			</ui-dialog-content>
		</ui-dialog>
	</content-wrapper>
</template>
